// Geography — master index
// Pulls together the 12 individual Geography content sections.
//
// Individual files:
// geographycontent1.js  → Section 1
// geographycontent2.js  → Section 2
// ...
// geographycontent12.js → Section 12
//
// This file is deliberately defensive because the Geography content files may use
// slightly different shapes while the subject is being built.
// It supports both:
//
// 1) Flat practice arrays:
//    practice: [
//      { type: 'mcq', question: '...', options: [...], correct: 1 }
//    ]
//
// 2) Tiered practice banks:
//    practice: {
//      warmUp: [],
//      level1: [],
//      level2: [],
//      level3: [],
//      examStyle: [],
//      challenge: []
//    }

import * as geo1 from './geographycontent1';
import * as geo2 from './geographycontent2';
import * as geo3 from './geographycontent3';
import * as geo4 from './geographycontent4';
import * as geo5 from './geographycontent5';
import * as geo6 from './geographycontent6';
import * as geo7 from './geographycontent7';
import * as geo8 from './geographycontent8';
import * as geo9 from './geographycontent9';
import * as geo10 from './geographycontent10';
import * as geo11 from './geographycontent11';
import * as geo12 from './geographycontent12';

const SECTION_META = [
  {
    id: 1,
    slug: 'locational-knowledge',
    title: 'Locational Knowledge',
    color: 'emerald',
    icon: 'Globe',
    description:
      'Continents, oceans, biomes, Africa, India, China, Russia and the Middle East.',
    aqaPaper: 'Paper 3',
  },
  {
    id: 2,
    slug: 'tectonic-activity',
    title: 'Tectonic Activity',
    color: 'rose',
    icon: 'Mountains',
    description:
      'Plate tectonics, earthquakes, volcanoes and surviving tectonic hazards.',
    aqaPaper: 'Paper 1',
  },
  {
    id: 3,
    slug: 'rocks-weathering-soil',
    title: 'Rocks, Weathering and Soil',
    color: 'amber',
    icon: 'Stack',
    description:
      'Rock types, weathering processes and how soils form.',
    aqaPaper: 'Paper 1',
  },
  {
    id: 4,
    slug: 'weather-and-climate',
    title: 'Weather and Climate',
    color: 'sky',
    icon: 'CloudRain',
    description:
      'Climate change, world climate zones, microclimates, weather forecasting and the water cycle.',
    aqaPaper: 'Paper 1',
  },
  {
    id: 5,
    slug: 'hydrology-coasts-glaciation',
    title: 'Hydrology, Coasts and Glaciation',
    color: 'blue',
    icon: 'Waves',
    description:
      'Drainage basins, rivers, flooding, coasts and glacial landscapes.',
    aqaPaper: 'Paper 1',
  },
  {
    id: 6,
    slug: 'population-and-urbanisation',
    title: 'Population and Urbanisation',
    color: 'violet',
    icon: 'UsersThree',
    description:
      'Population distribution, growth, migration and how cities expand.',
    aqaPaper: 'Paper 2',
  },
  {
    id: 7,
    slug: 'development',
    title: 'Development',
    color: 'emerald',
    icon: 'TrendUp',
    description:
      'Measuring development, the development gap, world trade and aid.',
    aqaPaper: 'Paper 2',
  },
  {
    id: 8,
    slug: 'economic-activity-resources',
    title: 'Economic Activity and Natural Resources',
    color: 'orange',
    icon: 'Factory',
    description:
      'Industry classification, energy, water, food and the management of natural resources.',
    aqaPaper: 'Paper 2',
  },
  {
    id: 9,
    slug: 'influencing-the-environment',
    title: 'Influencing the Environment',
    color: 'lime',
    icon: 'Leaf',
    description:
      'Pollution, acid rain, global warming, soil erosion, conservation and environmental management.',
    aqaPaper: 'Paper 1',
  },
  {
    id: 10,
    slug: 'contrasting-regions',
    title: 'Case Studies — Nigeria and Japan',
    color: 'pink',
    icon: 'MapPin',
    description:
      'Two contrasting regions compared: Nigeria as a newly emerging economy and Japan as a high-income country.',
    aqaPaper: 'Paper 2',
  },
  {
    id: 11,
    slug: 'enquiry-and-skills',
    title: 'Geographical Enquiry and Skills',
    color: 'teal',
    icon: 'MagnifyingGlass',
    description:
      'Fieldwork, sampling, OS maps, graphs, map interpretation and geographical enquiry.',
    aqaPaper: 'Paper 3',
  },
  {
    id: 12,
    slug: 'exam-practice',
    title: 'Exam Practice',
    color: 'indigo',
    icon: 'GraduationCap',
    description:
      'AQA Papers 1, 2 and 3 — structure, timing, command words and exam technique.',
    aqaPaper: 'All',
  },
];

const sectionModules = [
  geo1,
  geo2,
  geo3,
  geo4,
  geo5,
  geo6,
  geo7,
  geo8,
  geo9,
  geo10,
  geo11,
  geo12,
];

export const GEOGRAPHY_PRACTICE_TIERS = [
  'warmUp',
  'level1',
  'level2',
  'level3',
  'examStyle',
  'challenge',
];

function getPossibleSection(module, sectionNumber) {
  if (!module) return null;

  const candidates = [
    module.default,
    module[`geographyContent${sectionNumber}`],
    module[`geographycontent${sectionNumber}`],
    module[`geographySection${sectionNumber}`],
    module[`geographysection${sectionNumber}`],
    module[`section${sectionNumber}`],
  ];

  for (const candidate of candidates) {
    if (!candidate) continue;

    // Some section files (sections 3 and 4) export as `[{ id, topics: [...] }]` —
    // an array wrapping a single section object. Unwrap it so the loader treats
    // the inner object as the section descriptor consistently with the other files.
    if (Array.isArray(candidate)) {
      if (
        candidate.length > 0 &&
        candidate[0] &&
        typeof candidate[0] === 'object' &&
        !Array.isArray(candidate[0]) &&
        Array.isArray(candidate[0].topics)
      ) {
        return candidate[0];
      }

      return candidate;
    }

    return candidate;
  }

  return null;
}

function extractTopics(module, sectionNumber) {
  if (!module) return [];

  const possibleSection = getPossibleSection(module, sectionNumber);

  // Preferred shape: the section object directly carries `topics: [...]`
  if (
    possibleSection &&
    typeof possibleSection === 'object' &&
    !Array.isArray(possibleSection) &&
    Array.isArray(possibleSection.topics)
  ) {
    return possibleSection.topics;
  }

  const namedTopics =
    module[`section${sectionNumber}Topics`] ||
    module[`geographyContent${sectionNumber}Topics`] ||
    module[`geographycontent${sectionNumber}Topics`] ||
    module[`geographyTopics${sectionNumber}`];

  if (Array.isArray(namedTopics)) return namedTopics;

  // Fallback: the module default is itself a topics array
  if (Array.isArray(module.default)) return module.default;

  if (Array.isArray(possibleSection)) return possibleSection;

  return [];
}

function normalisePracticeToTieredBank(practice) {
  const emptyBank = {
    warmUp: [],
    level1: [],
    level2: [],
    level3: [],
    examStyle: [],
    challenge: [],
  };

  if (!practice) return emptyBank;

  // Existing geographycontent files may use:
  // practice: [...]
  // In that case, expose the same questions through warmUp and level1 so
  // the topic page does not show an empty tier.
  if (Array.isArray(practice)) {
    return {
      warmUp: practice,
      level1: practice,
      level2: [],
      level3: [],
      examStyle: [],
      challenge: [],
    };
  }

  // Tiered structure.
  if (typeof practice === 'object') {
    return {
      warmUp: Array.isArray(practice.warmUp) ? practice.warmUp : [],
      level1: Array.isArray(practice.level1) ? practice.level1 : [],
      level2: Array.isArray(practice.level2) ? practice.level2 : [],
      level3: Array.isArray(practice.level3) ? practice.level3 : [],
      examStyle: Array.isArray(practice.examStyle) ? practice.examStyle : [],
      challenge: Array.isArray(practice.challenge) ? practice.challenge : [],
    };
  }

  return emptyBank;
}

export function getGeographyPracticeBank(topic, tier = 'warmUp') {
  const bank = normalisePracticeToTieredBank(topic?.practice);

  if (bank[tier]?.length) return bank[tier];

  // Sensible fallback order so a page does not appear empty simply because
  // a specific tier has not been authored yet.
  if (bank.warmUp.length) return bank.warmUp;
  if (bank.level1.length) return bank.level1;
  if (bank.level2.length) return bank.level2;
  if (bank.level3.length) return bank.level3;
  if (bank.examStyle.length) return bank.examStyle;
  if (bank.challenge.length) return bank.challenge;

  return [];
}

export function getGeographyPracticeCount(topic) {
  if (!topic) return 0;

  const practice = topic.practice;

  if (Array.isArray(practice)) {
    return practice.length;
  }

  if (practice && typeof practice === 'object') {
    const bank = normalisePracticeToTieredBank(practice);

    return GEOGRAPHY_PRACTICE_TIERS.reduce(
      (total, tier) => total + (bank[tier]?.length || 0),
      0
    );
  }

  return 0;
}

function normaliseQuestion(question, index = 0, topicId = 'geography-topic') {
  if (!question || typeof question !== 'object') return question;

  const id = question.id || `${topicId}-q-${index + 1}`;
  const type = question.type || 'mcq';

  // MCQ geography files commonly use:
  // correct: 1
  // where 1 is the correct option index.
  if (type === 'mcq' || type === 'multiple-choice' || type === 'choice') {
    const correctIndex =
      typeof question.correct === 'number'
        ? question.correct
        : typeof question.correctIndex === 'number'
          ? question.correctIndex
          : typeof question.answerIndex === 'number'
            ? question.answerIndex
            : null;

    const correctAnswer =
      question.correctAnswer ||
      question.answer ||
      question.expected_answer ||
      (correctIndex !== null ? question.options?.[correctIndex] : undefined);

    return {
      ...question,
      id,
      type: 'mcq',
      correctIndex,
      correctAnswer,
      answer: question.answer || correctAnswer,
    };
  }

  // True/false geography files commonly use:
  // correct: true
  if (type === 'true-false' || type === 'truefalse' || type === 'boolean') {
    const booleanAnswer =
      typeof question.correct === 'boolean'
        ? question.correct
        : typeof question.answer === 'boolean'
          ? question.answer
          : typeof question.correctAnswer === 'boolean'
            ? question.correctAnswer
            : null;

    return {
      ...question,
      id,
      type: 'true-false',
      correct: booleanAnswer,
      correctAnswer:
        booleanAnswer === null
          ? question.correctAnswer
          : booleanAnswer
            ? 'True'
            : 'False',
      answer:
        booleanAnswer === null
          ? question.answer
          : booleanAnswer
            ? 'True'
            : 'False',
    };
  }

  // Short-answer geography files commonly use:
  // answer: 'Pacific Ocean'
  if (type === 'short-answer' || type === 'short' || type === 'text') {
    const correctAnswer =
      question.correctAnswer ||
      question.answer ||
      question.expected_answer ||
      question.modelAnswer ||
      '';

    return {
      ...question,
      id,
      type: 'short-answer',
      correctAnswer,
      answer: correctAnswer,
      acceptedAnswers: question.acceptedAnswers || question.accepted_answers || [],
    };
  }

  return {
    ...question,
    id,
  };
}

export function getNormalisedGeographyPracticeBank(topic, tier = 'warmUp') {
  return getGeographyPracticeBank(topic, tier).map((question, index) =>
    normaliseQuestion(
      question,
      index,
      topic?.id || topic?.slug || 'geography-topic'
    )
  );
}

function normaliseTopic(topic, sectionMeta, sectionExtras = {}) {
  if (!topic || typeof topic !== 'object') return topic;

  const synthesisedPractice = synthesisePracticeFromTopic(topic, sectionExtras);
  const normalisedPractice = normalisePracticeToTieredBank(synthesisedPractice);

  const synthesisedCount = GEOGRAPHY_PRACTICE_TIERS.reduce(
    (total, tier) => total + (normalisedPractice[tier]?.length || 0),
    0
  );

  return {
    ...topic,
    practice: synthesisedPractice,
    sectionId: sectionMeta.id,
    sectionSlug: sectionMeta.slug,
    sectionTitle: sectionMeta.title,
    aqaPaper: topic.aqaPaper || sectionMeta.aqaPaper,
    practiceBank: normalisedPractice,
    questionCount:
      synthesisedCount +
      (Array.isArray(topic.questions) ? topic.questions.length : 0) +
      (Array.isArray(topic.quiz) ? topic.quiz.length : 0),
  };
}

// Convert topics that use the rich `quickCheck` / `workedExamples` /
// `sectionPractice.objectiveQuestions` schema into the tiered practice bank
// shape expected by the `GeographyPractice.js` page (warmUp / level1 / level2 /
// level3 / examStyle). Topics that already supply a tiered `practice` object
// (or a flat practice array) are returned untouched.
function synthesisePracticeFromTopic(topic, sectionExtras = {}) {
  if (!topic) return null;

  // Tiered practice already present — respect the authored shape.
  if (
    topic.practice &&
    typeof topic.practice === 'object' &&
    !Array.isArray(topic.practice)
  ) {
    const existing = topic.practice;
    const hasAnyTier = GEOGRAPHY_PRACTICE_TIERS.some(
      (tier) => Array.isArray(existing[tier]) && existing[tier].length > 0
    );
    if (hasAnyTier) return existing;
  }

  // Flat practice array — keep the original shape so downstream code that
  // already handled it continues to work.
  if (Array.isArray(topic.practice) && topic.practice.length > 0) {
    return topic.practice;
  }

  const quickCheck = Array.isArray(topic.quickCheck)
    ? topic.quickCheck
        .filter(
          (item) =>
            item &&
            typeof item === 'object' &&
            (item.question || item.prompt) &&
            Array.isArray(item.options)
        )
        .map((item, index) => ({
          ...item,
          id: item.id || `${topic.id || topic.slug || 'geo-topic'}-qc-${index + 1}`,
          type: 'mcq',
        }))
    : [];

  const workedExamples = Array.isArray(topic.workedExamples)
    ? topic.workedExamples
        .filter((ex) => ex && (ex.question || ex.prompt || ex.label))
        .map((ex, index) => ({
          id: `${topic.id || topic.slug || 'geo-topic'}-we-${index + 1}`,
          type: 'short-answer',
          question: ex.question || ex.prompt || ex.label,
          answer: ex.answer || '',
          correctAnswer: ex.answer || '',
          explanation: ex.answer || '',
        }))
    : [];

  const sectionObjectives = Array.isArray(sectionExtras.objectiveQuestions)
    ? sectionExtras.objectiveQuestions
        .filter(
          (item) =>
            item &&
            typeof item === 'object' &&
            item.question &&
            Array.isArray(item.options)
        )
        .map((item, index) => ({
          ...item,
          id: item.id || `${topic.sectionSlug || 'geo'}-obj-${index + 1}`,
          type: 'mcq',
        }))
    : [];

  const sectionExamStyle = Array.isArray(sectionExtras.examStyleQuestions)
    ? sectionExtras.examStyleQuestions
        .filter((item) => item && typeof item === 'object' && item.question)
        .map((item, index) => ({
          id: `${topic.sectionSlug || 'geo'}-exam-${index + 1}`,
          type: 'short-answer',
          question:
            item.marks && item.command
              ? `[${item.marks} marks · ${item.command}] ${item.question}`
              : item.question,
          answer: item.indicativeContent || '',
          correctAnswer: item.indicativeContent || '',
          explanation: item.indicativeContent || '',
        }))
    : [];

  // Tier mapping:
  //   warmUp  ← topic.quickCheck (recall MCQs)
  //   level1  ← topic.workedExamples (short-answer Q&As)
  //   level2  ← section.sectionPractice.objectiveQuestions (broader MCQs)
  //              fallback to quickCheck so the tier is never empty.
  //   level3  ← section.sectionPractice.examStyleQuestions (exam essays)
  //              fallback to workedExamples.
  return {
    warmUp: quickCheck,
    level1: workedExamples.length ? workedExamples : quickCheck,
    level2: sectionObjectives.length ? sectionObjectives : quickCheck,
    level3: sectionExamStyle.length ? sectionExamStyle : workedExamples,
    examStyle: sectionExamStyle,
    challenge: [],
  };
}

function buildSection(module, meta, index) {
  const sectionNumber = index + 1;
  const possibleSection = getPossibleSection(module, sectionNumber);

  const sectionExtras = {
    objectiveQuestions:
      possibleSection?.sectionPractice?.objectiveQuestions || [],
    examStyleQuestions:
      possibleSection?.sectionPractice?.examStyleQuestions || [],
    warmUpQuestions:
      possibleSection?.sectionPractice?.warmUpQuestions || [],
  };

  if (
    possibleSection &&
    typeof possibleSection === 'object' &&
    !Array.isArray(possibleSection) &&
    Array.isArray(possibleSection.topics)
  ) {
    const topics = possibleSection.topics.map((topic) =>
      normaliseTopic(topic, meta, sectionExtras)
    );

    return {
      ...meta,
      ...possibleSection,
      ...meta,
      topics,
      status: possibleSection.status || 'live',
      topicCount: topics.length,
    };
  }

  const topics = extractTopics(module, sectionNumber).map((topic) =>
    normaliseTopic(topic, meta, sectionExtras)
  );

  return {
    ...meta,
    topics,
    status: 'live',
    topicCount: topics.length,
  };
}

export const geographySections = sectionModules.map((module, index) =>
  buildSection(module, SECTION_META[index], index)
);

export const getTotalGeographyTopics = () =>
  geographySections.reduce(
    (acc, section) => acc + (section.topics?.length || 0),
    0
  );

export const getTotalGeographyQuestions = () =>
  geographySections.reduce((sectionTotal, section) => {
    const topicQuestionCount = (section.topics || []).reduce(
      (topicTotal, topic) => topicTotal + (topic.questionCount || 0),
      0
    );

    return sectionTotal + topicQuestionCount;
  }, 0);

export const findGeographyTopic = (sectionSlug, topicSlug) => {
  const section = geographySections.find((s) => s.slug === sectionSlug);

  if (!section) {
    return { section: null, topic: null };
  }

  const topic = (section.topics || []).find((t) => t.slug === topicSlug);

  return {
    section,
    topic: topic || null,
  };
};

export const getGeographySectionBySlug = (slug) =>
  geographySections.find((section) => section.slug === slug) || null;

export const getGeographyTopicBySlug = (topicSlug) => {
  for (const section of geographySections) {
    const topic = (section.topics || []).find((t) => t.slug === topicSlug);

    if (topic) {
      return { section, topic };
    }
  }

  return { section: null, topic: null };
};

export const getGeographySectionsByPaper = (paper) =>
  geographySections.filter((section) => section.aqaPaper === paper);

export const getLiveGeographySections = () =>
  geographySections.filter((section) => section.status !== 'draft');

export const getGeographySectionSummary = () =>
  geographySections.map((section) => ({
    id: section.id,
    slug: section.slug,
    title: section.title,
    description: section.description,
    aqaPaper: section.aqaPaper,
    topicCount: section.topics?.length || 0,
    questionCount: (section.topics || []).reduce(
      (total, topic) => total + (topic.questionCount || 0),
      0
    ),
    status: section.status || 'live',
  }));

export default geographySections;